import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { AuthContext } from './AuthContext'
import { makeCan } from './permissions'
import { qk } from '../lib/queryKeys'
import { setSessionExpiredHandler } from '../lib/apiClient'
import { logoutSession } from '../lib/refresh'
import {
  subscribe,
  getSession,
  setSession,
  mergeUser,
  clearSession,
  getPreviewRoleId,
  setPreviewRoleId,
  getPreviewUserId,
  setPreviewUserId,
} from '../lib/tokenStore'
import * as authApi from '../api/auth'
import * as usersApi from '../api/users'

const emptySnapshot = () => null

const HEARTBEAT_MS = 60_000
const IDLE_AFTER_MS = 5 * 60_000
const INPUT_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'scroll', 'touchstart']

export default function AuthProvider({ children }) {
  const session = useSyncExternalStore(subscribe, getSession, emptySnapshot)
  const navigate = useNavigate()
  const qc = useQueryClient()
  // View-as-role (profile menu → Roles) or view-as-user (Activity → View).
  // Picking or leaving either reloads the page, so this is fixed for the page's
  // life and no admin-view cache survives.
  const [previewRoleId] = useState(getPreviewRoleId)
  const [previewUserId] = useState(getPreviewUserId)
  const previewing = previewRoleId != null || previewUserId != null

  // apiClient calls this when a refresh fails outright. Registered here so the
  // HTTP layer never has to know about the router.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      clearSession()
      qc.clear()
      navigate('/auth/login?expired=1', { replace: true })
    })
    return () => setSessionExpiredHandler(() => {})
  }, [navigate, qc])

  // Permission bootstrap. This is a query rather than a useEffect fetch on
  // purpose: TanStack Query dedupes, so React 19 StrictMode's double mount
  // issues ONE request instead of two. The server resolves tags from the role
  // on every request, so a short staleTime + refetch on focus is what makes an
  // Access Control change reach an already signed-in user.
  const me = useQuery({
    queryKey: qk.auth.me(),
    queryFn: () => usersApi.me().then((r) => r.data),
    enabled: Boolean(session?.access_token),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
  })

  // Fold `permissions` into the stored session so a page reload has them before
  // /users/me resolves — otherwise the nav flickers on every refresh. A previewed
  // role's tags stay out of it: the stored session is shared with other tabs.
  useEffect(() => {
    if (me.data && !me.data.preview) mergeUser(me.data)
  }, [me.data])

  // The previewed role/user was deactivated, deleted or out-grew the admin's own
  // tags since it was picked: drop back to the admin's own view.
  useEffect(() => {
    if (previewing && me.isError) {
      setPreviewRoleId(null)
      setPreviewUserId(null)
      window.location.assign('/')
    }
  }, [previewing, me.isError])

  // Online status → users.active_session, and each `true` ping is one minute of
  // presence history (backend users/views.py heartbeat). True on load, every 60s
  // while the tab is visible AND the user touched it in the last 5 minutes, and
  // at once when they come back; false when the tab is hidden, closed or goes
  // idle. A tab that dies without a word stops pinging and the Activity screen
  // drops it after 2 minutes. Idle tabs not pinging also lets sessions expire.
  const signedIn = Boolean(session?.access_token)
  useEffect(() => {
    if (!signedIn) return
    const ping = (state, opts) => usersApi.heartbeat(state, opts).catch(() => {})
    let lastInput = Date.now()
    let idle = false
    const onInput = () => {
      lastInput = Date.now()
      if (idle && document.visibilityState === 'visible') {
        idle = false
        ping(true)
      }
    }
    const onVisibility = () => {
      const visible = document.visibilityState === 'visible'
      if (visible) {
        lastInput = Date.now()
        idle = false
      }
      ping(visible, { keepalive: !visible })
    }
    ping(true)
    const timer = setInterval(() => {
      if (document.visibilityState !== 'visible') return
      if (Date.now() - lastInput > IDLE_AFTER_MS) {
        if (!idle) {
          idle = true
          ping(false)
        }
        return
      }
      ping(true)
    }, HEARTBEAT_MS)
    document.addEventListener('visibilitychange', onVisibility)
    INPUT_EVENTS.forEach((e) => window.addEventListener(e, onInput, { passive: true, capture: true }))
    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisibility)
      INPUT_EVENTS.forEach((e) => window.removeEventListener(e, onInput, { capture: true }))
    }
  }, [signedIn])

  const signIn = useCallback(
    async (email, password) => {
      setPreviewRoleId(null)
      setPreviewUserId(null)
      const res = await authApi.login(email, password)
      setSession(res)
      // Refetch /users/me for the new user; the old one's permissions must not leak.
      await qc.invalidateQueries({ queryKey: qk.auth.me() })
      return res
    },
    [qc],
  )

  const signOut = useCallback(() => {
    // The refresh token is an HttpOnly cookie JS cannot delete, so ask the server
    // to clear it. Not awaited: sign-out must be instant even when offline.
    // The offline ping carries its token explicitly — clearSession runs next.
    const token = getSession()?.access_token
    if (token) usersApi.heartbeat(false, { token, keepalive: true }).catch(() => {})
    logoutSession()
    clearSession()
    qc.clear()
    navigate('/auth/login', { replace: true })
  }, [navigate, qc])

  // Throws the server's reason (e.g. a role holding tags the admin lacks) before
  // anything is stored, so a refused pick never lands the tab in a broken view.
  // Switching reloads the page the admin is on; a page the new view can't open
  // shows the route guard's "no access" state rather than bouncing to home.
  const startPreview = useCallback(async (roleId) => {
    await usersApi.meAsRole(roleId)
    setPreviewUserId(null)
    setPreviewRoleId(roleId)
    window.location.reload()
  }, [])

  const startUserPreview = useCallback(async (userId) => {
    await usersApi.meAsUser(userId)
    setPreviewRoleId(null)
    setPreviewUserId(userId)
    window.location.reload()
  }, [])

  const stopPreview = useCallback(() => {
    setPreviewRoleId(null)
    setPreviewUserId(null)
    window.location.reload()
  }, [])

  // While previewing, the stored session holds the ADMIN's user and tags; never
  // fall back to it, or the admin's nav flashes before the previewed view arrives.
  const cached = previewing ? null : session?.data
  const user = me.data ?? cached ?? null
  const permissions = me.data?.permissions ?? cached?.permissions ?? null
  const preview = me.data?.preview ?? null

  const status = !session?.access_token
    ? 'anonymous'
    : me.isError
      ? previewing ? 'loading' : 'error'
      : me.data || permissions
        ? 'authenticated'
        : 'loading'

  const value = useMemo(
    () => ({
      session,
      user,
      permissions,
      status,
      can: makeCan(permissions),
      isAuthenticated: status === 'authenticated',
      signIn,
      signOut,
      setSession,
      preview,
      startPreview,
      startUserPreview,
      stopPreview,
    }),
    [session, user, permissions, status, signIn, signOut, preview, startPreview, startUserPreview, stopPreview],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
